import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Briefcase, Calendar, BookOpen, X, CreditCard } from 'lucide-react';
import PaymentProcessor from './PaymentProcessor';

const ServiceOrderForm = ({ isOpen, onClose, serviceType, selectedPlan }) => {
  const [step, setStep] = useState(1); // 1: Details, 2: Payment, 3: Confirmation
  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    serviceDetails: '',
    startDate: '',
    additionalRequests: ''
  });
  const [paymentResult, setPaymentResult] = useState(null);

  const serviceIcons = {
    jobs: <Briefcase className="h-6 w-6" />,
    events: <Calendar className="h-6 w-6" />,
    blog: <BookOpen className="h-6 w-6" />
  };

  const serviceNames = {
    jobs: 'Job Posting',
    events: 'Event Listing', 
    blog: 'Blog Package'
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNextStep = () => {
    setStep(2);
  };

  const handlePaymentSuccess = (result) => {
    setPaymentResult(result);
    setStep(3);
  };

  const handlePaymentCancel = () => {
    setStep(1);
  };

  const getServicePrice = () => {
    if (!selectedPlan) return 0;
    
    // Extract price from selectedPlan.price (e.g., "£150" -> 150)
    const priceStr = selectedPlan.price.replace('£', '').replace(',', '');
    return parseFloat(priceStr);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              {serviceIcons[serviceType]}
            </div>
            <div>
              <h2 className="text-xl font-bold text-charcoal">
                {serviceNames[serviceType]} Order
              </h2>
              <p className="text-sm text-gray-600">
                {selectedPlan?.name} - {selectedPlan?.price}{selectedPlan?.period}
              </p>
            </div>
          </div>
          <Button variant="ghost" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Step 1: Service Details */}
        {step === 1 && (
          <div className="p-6 space-y-6">
            <div className="bg-primary/5 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Selected Package</h3>
              <div className="text-sm space-y-1">
                <p><strong>Service:</strong> {selectedPlan?.name}</p>
                <p><strong>Price:</strong> {selectedPlan?.price}{selectedPlan?.period}</p>
                {selectedPlan?.features && (
                  <div className="mt-2">
                    <p><strong>Includes:</strong></p>
                    <ul className="list-disc list-inside text-xs space-y-1 mt-1">
                      {selectedPlan.features.slice(0, 3).map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Business Name *</Label>
                <Input
                  value={formData.businessName}
                  onChange={(e) => handleInputChange('businessName', e.target.value)}
                  placeholder="Your Business Name"
                  required
                />
              </div>
              <div>
                <Label>Contact Name *</Label>
                <Input
                  value={formData.contactName}
                  onChange={(e) => handleInputChange('contactName', e.target.value)}
                  placeholder="Your Full Name"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="contact@business.com"
                  required
                />
              </div>
              <div>
                <Label>Phone Number</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="01234 567890"
                />
              </div>
            </div>

            <div>
              <Label>Service Start Date</Label>
              <Input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div>
              <Label>
                {serviceType === 'jobs' && 'Job Details'}
                {serviceType === 'events' && 'Event Details'}
                {serviceType === 'blog' && 'Content Requirements'}
              </Label>
              <Textarea
                value={formData.serviceDetails}
                onChange={(e) => handleInputChange('serviceDetails', e.target.value)}
                placeholder={
                  serviceType === 'jobs' 
                    ? "Job title, requirements, salary, location..."
                    : serviceType === 'events'
                    ? "Event name, date, time, location, description..."
                    : "Blog topics, target audience, content preferences..."
                }
                rows={4}
              />
            </div>

            <div>
              <Label>Additional Requests</Label>
              <Textarea
                value={formData.additionalRequests}
                onChange={(e) => handleInputChange('additionalRequests', e.target.value)}
                placeholder="Any special requirements or additional services needed..."
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleNextStep}
                disabled={!formData.businessName || !formData.contactName || !formData.email}
                className="bg-primary hover:bg-primary/90"
              >
                Proceed to Payment
                <CreditCard className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Payment */}
        {step === 2 && (
          <div className="p-6">
            <PaymentProcessor
              amount={getServicePrice()}
              orderDetails={{
                package: `${serviceNames[serviceType]} - ${selectedPlan?.name}`,
                setupFee: 'Included'
              }}
              onSuccess={handlePaymentSuccess}
              onCancel={handlePaymentCancel}
              customerEmail={formData.email}
            />
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && (
          <div className="p-6 text-center space-y-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CreditCard className="h-8 w-8 text-green-600" />
            </div>
            
            <div>
              <h3 className="text-xl font-bold text-green-600 mb-2">Order Confirmed!</h3>
              <p className="text-gray-600">
                Your {serviceNames[serviceType].toLowerCase()} order has been received and payment processed.
              </p>
            </div>

            <div className="bg-green-50 p-4 rounded-lg text-left max-w-md mx-auto">
              <h4 className="font-semibold mb-2">Order Details</h4>
              <div className="text-sm space-y-1">
                <p><strong>Service:</strong> {selectedPlan?.name}</p>
                <p><strong>Business:</strong> {formData.businessName}</p>
                <p><strong>Amount Paid:</strong> £{getServicePrice()}</p>
                <p><strong>Transaction ID:</strong> {paymentResult?.transactionId}</p>
                <p><strong>Payment Method:</strong> {paymentResult?.method}</p>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-800">
              <p><strong>What happens next?</strong></p>
              <ul className="text-left mt-2 space-y-1">
                <li>• You will receive a confirmation email within 5 minutes</li>
                <li>• Our team will contact you within 24 hours to set up your service</li>
                <li>• Service will be activated according to your selected start date</li>
                {serviceType === 'jobs' && <li>• Your job posting will go live within 48 hours</li>}
                {serviceType === 'events' && <li>• Your event will be featured on our platform</li>}
                {serviceType === 'blog' && <li>• Content creation will begin as per your requirements</li>}
              </ul>
            </div>

            <Button onClick={onClose} className="bg-primary hover:bg-primary/90">
              Close
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceOrderForm;